import {
  faAngleDown,
  faAngleUp,
  faTimes,
  faHospitalAlt,
  faExchange
} from '@fortawesome/pro-regular-svg-icons';
import { faGift } from '@fortawesome/pro-solid-svg-icons';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import styles from './styles.module.scss';
import { AlertManager } from '../Alert/index.js';
import AppBar from '../AppBar/index.js';
import Button from '../Button/index.ts';
import Drawer from '../Drawer/index.js';
import ErrorBoundary from '../ErrorBoundary/index.ts';
import Icon from '../Icon/index.ts';
import Input, { useSearch } from '../Input/index.ts';
import List, { ListItem, ListItemIcon, ListItemText } from '../List/index.ts';
import Popover from '../Popover/index.js';
import { SnackbarManager } from '../Snackbar/index.js';
import Spacer from '../Spacer/index.ts';
import type { InferPropTypes } from '../typings/index.ts';
import { useUpdatesIndicator, Update, UpdatesModal } from './UpdatesModal/index.ts';
import addVariants from '../utils/addVariants/index.ts';
import searchObject from '../utils/searchObject/index.ts';
import useBoolean from '../utils/useBoolean/index.ts';

export const VARIANTS = ['default', 'no-border'] as const;
export type GlobalNavigationVariant = (typeof VARIANTS)[number];
export interface NavigationLink {
  /**
   * Name of the application
   */
  label: string;
  /**
   * Application URL
   */
  href?: string | null;
  /**
   * Whether is the current application
   */
  isActive?: boolean | null;
  /**
   * Fired on click of application
   */
  onClick?: ((event: React.SyntheticEvent) => void) | null;
}

export interface Organization {
  [key: string]: unknown;
  /**
   * The organization's id
   */
  id: string;
  /**
   * Whether the organization is the current one
   */
  isActive?: boolean | null;
  /**
   * Special className to add
   */
  className?: string | null;
  /**
   * Organization title
   */
  label: string;
  /**
   * To navigate to the organization
   */
  onClick?: ((event: React.SyntheticEvent) => void) | null;
  /**
   * To navigate to the organization
   */
  href?: string | null;
}

const propTypes = {
  /**
   * Any children to be rendered
   */
  children: PropTypes.node.isRequired,
  /**
   * All the organizations the user is apart of
   */
  organizations: PropTypes.arrayOf<Organization>(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      href: PropTypes.string,
      isActive: PropTypes.bool,
      onClick: PropTypes.func
    }).isRequired
  ),
  /**
   * Applications for the user to navigate to
   */
  links: PropTypes.arrayOf<NavigationLink>(
    PropTypes.shape({
      isActive: PropTypes.bool,
      className: PropTypes.string,
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func,
      href: PropTypes.string
    }).isRequired
  ),
  /**
   * Latest updates to the application and where the user can learn more
   */
  updates: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      url: PropTypes.string.isRequired,
      title: PropTypes.node.isRequired,
      description: PropTypes.node.isRequired
    }).isRequired
  ) as PropTypes.Validator<Update[]>,
  /**
   * Decides the variant of the list item
   */
  variant: PropTypes.oneOfType([
    PropTypes.oneOf<GlobalNavigationVariant>(VARIANTS).isRequired,
    PropTypes.bool.isRequired,
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf<GlobalNavigationVariant>(VARIANTS).isRequired,
        PropTypes.bool.isRequired
      ]).isRequired
    )
  ]),
  /**
   * Additional classnames to apply
   */
  className: PropTypes.string,
  /**
   * The user's name (if provided will display account on right side)
   */
  userName: PropTypes.string,
  /**
   * The user's email
   */
  userEmail: PropTypes.string,
  /**
   * A hash of the user's email for gravatar
   */
  userEmailHash: PropTypes.string,
  /**
   * Where to redirect to on click of sign out btn
   */
  signOutLink: PropTypes.string,
  /**
   * Insert content on the right side of the global nav bar
   */
  right: PropTypes.node
};

const defaultProps = {
  organizations: [] as Organization[],
  variant: VARIANTS[0],
  className: '',
  userName: '',
  userEmail: '',
  userEmailHash: '',
  signOutLink: '/signout',
  links: [] as NavigationLink[],
  updates: [] as Update[],
  right: null
};

export type GlobalNavigationProps = InferPropTypes<
  typeof propTypes,
  typeof defaultProps,
  { updates?: Update[] }
>;

const ICONS = {
  CLOSE: faTimes,
  ORGANIZATION: faHospitalAlt,
  SWITCH: faExchange,
  OPEN: faAngleUp,
  CLOSED: faAngleDown,
  UPDATE: faGift
};

const GlobalNavigation: React.FC<GlobalNavigationProps> = ({
  children,
  organizations: organizationsFromProps,
  links,
  updates = defaultProps.updates,
  className,
  variant: variants,
  userName,
  userEmail,
  signOutLink,
  userEmailHash,
  right
}) => {
  const { hasUpdates, sawLatestUpdate, shouldShowUpdates, numUpdates, latestUpdate } =
    useUpdatesIndicator(updates);
  const {
    value: isUpdatesModalOpen,
    setTrue: openUpdatesModal,
    setFalse: closeUpdatesModal
  } = useBoolean(false);
  const {
    value: orgSwitcherActive,
    toggle: toggleOrgSwitcher,
    setFalse: closeDrawer
  } = useBoolean(false);
  const { setQuery, query } = useSearch('');
  const noBorder = ([] as (GlobalNavigationVariant | undefined | boolean)[])
    .concat(variants)
    .includes('no-border');

  const organizations = organizationsFromProps!
    .filter(searchObject(query))
    .sort(({ label: labelA }, { label: labelB }) => labelA.localeCompare(labelB));
  let orgSwitcherButton;

  if (Array.isArray(organizationsFromProps) && organizationsFromProps.length > 1) {
    orgSwitcherButton = (
      <Button
        className={styles['aglmd-global-nav__org-switcher__icon']}
        icon={ICONS.SWITCH}
        onClick={toggleOrgSwitcher}
        color="black"
        isSelectable
        isSelected={orgSwitcherActive}
      />
    );
  }

  return (
    <AlertManager>
      <div
        className={classnames(
          addVariants('aglmd-global-nav__wrapper', styles, variants),
          className
        )}
      >
        <AppBar
          className={addVariants('aglmd-global-nav', styles)}
          variant={['black', noBorder && 'no-border', 'no-padding']}
          isFixed
          left={
            <>
              {orgSwitcherButton}
              <img
                className={styles['aglmd-global-nav__logo']}
                src="https://d2p53dh3qxfm0x.cloudfront.net/assets/web-meta/img/badge-white-j5cl2zvr.png"
                alt="logo"
              />
              {links?.map(({ onClick, href, label, isActive }) => (
                <Button
                  key={label}
                  onClick={onClick}
                  href={href}
                  className={addVariants(
                    'aglmd-global-nav__link',
                    styles,
                    variants,
                    isActive && 'active'
                  )}
                  variant="square"
                  color="black"
                  isSelectable
                  isSelected={Boolean(isActive)}
                >
                  {label}
                </Button>
              ))}
            </>
          }
          right={
            <>
              {right}
              <Popover
                placement="bottom-start"
                variant={['no-padding', 'shadow']}
                className={styles['aglmd-global-nav__user__popover']}
                activator={({ isOpen, toggleOpen }) => (
                  <Button
                    icon={isOpen ? ICONS.OPEN : ICONS.CLOSED}
                    alignIcon="right"
                    variant="flat"
                    color="transparent"
                    onClick={() => {
                      toggleOpen();
                      sawLatestUpdate();
                    }}
                    className={classnames(
                      addVariants('aglmd-global-nav__user__button', styles, isOpen && 'active')
                    )}
                  >
                    {shouldShowUpdates && (
                      <Icon
                        icon={ICONS.UPDATE}
                        className={styles['aglmd-global-nav__update__indicator']}
                      />
                    )}
                    {userName}
                  </Button>
                )}
              >
                {userName && (
                  <div className={styles['aglmd-global-nav__user__info']}>
                    <div className={styles['aglmd-global-nav__user__info__image-container']}>
                      <img
                        className={styles['aglmd-global-nav__user__info__image']}
                        src={`https://gravatar.com/avatar/${userEmailHash}?s=40&d=mm`}
                        alt="profile"
                      />
                    </div>
                    <div className={styles['aglmd-global-nav__user__info__description']}>
                      <div className={styles['aglmd-global-nav__user__info__name']}>{userName}</div>
                      <div className={styles['aglmd-global-nav__user__info__email']}>
                        {userEmail}
                      </div>
                    </div>
                  </div>
                )}
                {hasUpdates && (
                  <Button
                    className={styles['aglmd-global-nav__user__link']}
                    onClick={numUpdates > 1 ? openUpdatesModal : undefined}
                    href={latestUpdate?.url}
                    variant={['flat', 'fill']}
                    icon={ICONS.UPDATE}
                    spacing={{ top: 1 }}
                  >
                    What&apos;s new on AgileMD
                  </Button>
                )}
                <Button
                  className={styles['aglmd-global-nav__user__link']}
                  href={signOutLink}
                  variant={['flat', 'fill']}
                  spacing={{ bottom: 1, top: hasUpdates ? 0 : 1 }}
                >
                  Sign out
                </Button>
              </Popover>
            </>
          }
        />
        <Drawer
          page={
            <ErrorBoundary>
              <SnackbarManager>{children}</SnackbarManager>
            </ErrorBoundary>
          }
          variant={['full-screen', 'black']}
          isOpen={orgSwitcherActive}
          className={styles['aglmd-global-nav__org-switcher']}
          width={290}
        >
          <Button
            onClick={closeDrawer}
            icon={ICONS.CLOSE}
            variant={['flat', 'large', 'square']}
            color="transparent"
            className={styles['aglmd-global-nav__org-switcher__close']}
          />
          <span className={styles['aglmd-global-nav__org-switcher__title']}>
            Organization Switcher
          </span>
          <Input
            placeholder="Search..."
            onChange={setQuery}
            className={styles['aglmd-global-nav__org-switch__search']}
            isClearable
          />
          {organizations.length ? (
            <List>
              {organizations.map(({ label, onClick, isActive, href, id }) => (
                <ListItem
                  key={id}
                  className={styles['aglmd-global-nav__organization__item']}
                  onClick={onClick}
                  href={href}
                  variant={['black']}
                  isSelected={isActive}
                >
                  <ListItemIcon icon={ICONS.ORGANIZATION} variant="comfortable" />
                  <ListItemText className={styles['aglmd-global-nav__organization__title']}>
                    {label}
                  </ListItemText>
                </ListItem>
              ))}
            </List>
          ) : (
            <Spacer y={2} x={2}>
              No organizations match your search query &quot;{query}&quot;
            </Spacer>
          )}
        </Drawer>
      </div>
      {isUpdatesModalOpen && <UpdatesModal updates={updates!} onClose={closeUpdatesModal} />}
    </AlertManager>
  );
};

GlobalNavigation.propTypes = propTypes;
GlobalNavigation.defaultProps = defaultProps;

export default GlobalNavigation;
